import React, {FC, useEffect, useMemo, useState} from 'react';
import {
    Box,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    ToggleButton,
    ToggleButtonGroup,
    Divider,
    Stack,
} from '@mui/material';
import {styled} from '@mui/material/styles';
import {tableCellClasses} from '@mui/material/TableCell';
import {useDescriptionAlert} from '../../../../share';
import {useDispatch} from 'react-redux';
import {
    createWoodGroupsList,
    fetchTreeMarking,
    fetchWoodData,
    WoodGroupsList
} from '../../../types/models/MergeGroups.ts';
import {useGetTreeMarkingPresentationQuery} from '../../../apis/TreeMarking.api.ts';
import {fixedFormat} from '../../../../utils/numberFormat.ts';
import {Trans} from '@lingui/react/macro';
import {useGetGroupQuery as useGetCordwoodGroupQuery} from '../../../apis/Cordwood.api.ts';
import {useGetGroupQuery as useGetLogGroupQuery} from '../../../apis/Log.api.ts';
import {StyledMdiIcon} from "../../../../share/components/styled/Table.tsx";
import {mdiLinkVariant} from "@mdi/js";
import TimberTypeLink from "../../timberType/TimberTypeDetail/TimberTypeLink.tsx";
import TimberStorageLink from "../../timberStorage/TimberStorageDetail/TimberStorageLink.tsx";
import GradientCircularProgress from "../../../../share/components/progress/GradientCircularProgress.tsx";
import { PieChart, BarChart } from '@mui/x-charts';
import { t } from "@lingui/core/macro";

const StyledTableCell = styled(TableCell)(({theme}) => ({
    [`&.${tableCellClasses.head}`]: {
        fontWeight: 'bold',
    },
    [`&.${tableCellClasses.body}`]: {
        backgroundColor: theme.palette.action.hover,
        fontSize: 14,
    },
}));

const StyledTableStorage = styled(TableCell)(({theme}) => ({
    [`&.${tableCellClasses.body}`]: {
        fontWeight: 'bold',
        borderBottom: 'none',
    },
}));

const StyledTableFooter = styled(TableCell)(({theme}) => ({
    [`&.${tableCellClasses.body}`]: {
        backgroundColor: theme.palette.action.hover,
        fontWeight: 'bold',
        fontSize: '1em',
    },
}));

// Helper types for aggregation
interface TypeAgg {
    count: number;
    volume: number;
    treeMarkingVolume: number
}

interface StorageAgg {
    [typeId: string]: TypeAgg
}

type IReportProps = {
    mainTitle: string;
    showStorage: boolean;
    cuttingId: string[];
    storageId: string[];
};

const ReportTreeMarkings: FC<IReportProps> = ({
                                                 mainTitle,
                                                 showStorage,
                                                 cuttingId = [],
                                                 storageId = [],
                                             }) => {

    const dispatch = useDispatch();
    const {openFailAlert} = useDescriptionAlert();

    const [groupedData, setGroupedData] = useState<WoodGroupsList>(createWoodGroupsList());
    const [treeMarkingData, setTreeMarkingData] = useState([]);

    const filter = {
        cuttingId,
        storageId,
    };

    // Fetch wood group data (same as GenericRootReport)
    const logData = useGetLogGroupQuery(filter as any);
    const cordwoodData = useGetCordwoodGroupQuery(filter as any);

    // Fetch TreeMarking and aggregate volumes by storage and type
    const treeMarkingOrderBys: any[] = [];
    const treeMarkingsQuery = useGetTreeMarkingPresentationQuery({
        list: {page: 0, pageSize: 100000},
        filter: {
            cuttingIdRefs: cuttingId,
            storageIdRefs: storageId,
            orderBys: treeMarkingOrderBys,
        },
    } as any, {skip: false});

    useEffect(() => {
        let mounted = true;
        if (!(logData.isFetching || cordwoodData.isFetching)) {
            fetchWoodData(
                dispatch,
                logData.data,
                cordwoodData.data,
            ).then((groups) => {
                if (mounted) {
                    groups.isLoading = false;
                    setGroupedData(groups)
                }
            }).catch((err) => openFailAlert(err?.message || 'Failed to load data'))
        }
        return () => {
            mounted = false
        };
    }, [logData.isFetching, cordwoodData.isFetching]);

    useEffect(() => {
        let mounted = true;
        if (!treeMarkingsQuery.isFetching) {
            fetchTreeMarking(
                dispatch,
                treeMarkingsQuery.data?.items || [],
            ).then((groups) => {
                if (mounted)
                    setTreeMarkingData(groups);
            }).catch((err) => openFailAlert(err?.message || 'Failed to load data'))
        }
        return () => {
            mounted = false
        };
    }, [treeMarkingsQuery.isFetching, treeMarkingsQuery.isLoading]);


    const treeMarkingAgg = useMemo(() => {
        const map: Record<string, StorageAgg> = {};
        const items: any[] = (treeMarkingData as any) || [];
        for (const a of items) {
            const sId = a.storageId?.toString?.() || String(a.storageId);
            const tId = a.timberTypeId?.toString?.() || String(a.timberTypeId);
            if (!map[sId]) map[sId] = {};
            if (!map[sId][tId]) map[sId][tId] = {count: 0, volume: 0, treeMarkingVolume: 0};
            map[sId][tId].treeMarkingVolume += a.volume || 0;
        }
        return map;
    }, [treeMarkingData]);

    // Reduce groupedData to storage+type sums
    const storageTypeAgg = useMemo(() => {
        const map: Record<string, StorageAgg & { logsVolume?: number; cordwoodVolume?: number }> = {} as any;
        for (const cuttingGroup of groupedData.items as any[]) {
            for (const storageGroup of cuttingGroup.items as any[]) {
                const sId = storageGroup.storage?.id?.toString?.() || String(storageGroup.storage?.id);
                if (!map[sId]) map[sId] = {} as any;
                for (const cls of storageGroup.items || []) {
                    const tId = cls.timberType?.id?.toString?.() || String(cls.timberType?.id);
                    if (!tId) continue;
                    if (!map[sId][tId]) map[sId][tId] = {
                        count: 0,
                        volume: 0,
                        treeMarkingVolume: 0,
                        logsVolume: 0,
                        cordwoodVolume: 0
                    } as any;
                    const isLogs = Array.isArray((cls as any)?.items); // logs have thickness items
                    (map[sId][tId] as any).count += (cls as any).count || 0;
                    (map[sId][tId] as any).volume += (cls as any).sum || 0;
                    if (isLogs) {
                        (map[sId][tId] as any).logsVolume += (cls as any).sum || 0;
                    } else {
                        (map[sId][tId] as any).cordwoodVolume += (cls as any).sum || 0;
                    }
                }
            }
        }
        // merge TreeMarking volumes
        for (const sId of Object.keys(treeMarkingAgg)) {
            if (!map[sId]) map[sId] = {} as any;
            for (const tId of Object.keys(treeMarkingAgg[sId])) {
                if (!map[sId][tId]) map[sId][tId] = {count: 0, volume: 0, treeMarkingVolume: 0, logsVolume: 0, cordwoodVolume: 0} as any;
                (map[sId][tId] as any).treeMarkingVolume += treeMarkingAgg[sId][tId].treeMarkingVolume;
            }
        }
        return map as Record<string, StorageAgg & { logsVolume?: number; cordwoodVolume?: number }>;
    }, [groupedData, treeMarkingAgg]);

    // Compute totals
    const totals = useMemo(() => {
        let count = 0, volume = 0, treeMarkingVolume = 0;
        for (const s of Object.values(storageTypeAgg)) {
            for (const v of Object.values(s)) {
                count += v.count;
                volume += v.volume;
                treeMarkingVolume += v.treeMarkingVolume;
            }
        }
        return {count, volume, treeMarkingVolume};
    }, [storageTypeAgg]);

    // Build label maps for nicer chart/category labels
    const storageLabels = useMemo(() => {
        if (!groupedData || !groupedData.items) return {};

        const map: Record<string, string> = {};
        for (const cg of (groupedData.items as any[])) {
            for (const sg of (cg.items as any[])) {
                const id = sg.storage?.id?.toString?.() || String(sg.storage?.id);
                const name = sg.storage?.name || id
                map[id] = name;
            }
        }

        const items: any[] = (treeMarkingData as any) || [];
        for (const a of items) {
            if(!map[a.storageId]) {
                map[a.storageId] = a.storage?.name || a.storageId?.toString?.() || String(a.storageId);
            }
        }
        return map;
    }, [groupedData, treeMarkingData]);

    const typeLabels = useMemo(() => {
        if (!groupedData || !groupedData.items) return {};

        const map: Record<string, string> = {};
        for (const cg of (groupedData.items as any[])) {
            for (const sg of (cg.items as any[])) {
                for (const cls of sg.items || []) {
                    const id = cls.timberType?.id?.toString?.() || String(cls.timberType?.id);
                    const name = cls.timberType?.name || id
                    map[id] = name;
                }
            }
        }
        // also include types from TreeMarkings only
        for (const sId of Object.keys(storageTypeAgg)) {
            for (const tId of Object.keys(storageTypeAgg[sId])) {
                if (!map[tId]) map[tId] = tId;
            }
        }

        const items: any[] = (treeMarkingData as any) || [];
        for (const a of items) {
            if(map[a.timberTypeId] === a.timberTypeId) {
                map[a.timberTypeId] = a.timberType?.name || a.timberTypeId?.toString?.() || String(a.timberTypeId);
            }
        }

        return map;
    }, [groupedData, storageTypeAgg, treeMarkingData]);

    type Metric = 'treeMarking' | 'volume' | 'diff' | 'count';
    const [metric, setMetric] = useState<Metric>('treeMarking');
    const [multiLevelPie, setMultiLevelPie] = useState<boolean>(false);

    const metricValue = (agg: TypeAgg): number => {
        switch (metric) {
            case 'treeMarking': return agg.treeMarkingVolume;
            case 'volume': return agg.volume;
            case 'diff': return agg.treeMarkingVolume - agg.volume;
            case 'count': return agg.count;
        }
    };

    // Prepare series for charts
    const chartData = useMemo(() => {
        // Determine domains
        const storageIds = Object.keys(storageTypeAgg);
        const typeIdsSet = new Set<string>();
        for (const sId of storageIds) {
            for (const tId of Object.keys(storageTypeAgg[sId] || {})) typeIdsSet.add(tId);
        }
        const typeIds = Array.from(typeIdsSet);

        const safeMetric = metric ?? 'treeMarking';
        const safeMetricValue = (agg: TypeAgg): number => {
            const v =
                safeMetric === 'treeMarking' ? agg.treeMarkingVolume :
                    safeMetric === 'volume' ? agg.volume :
                        safeMetric === 'diff' ? (agg.treeMarkingVolume - agg.volume) :
                            agg.count;
            const n = Number(v);
            return Number.isFinite(n) ? n : 0;
        };

        // Common: totals for inner/outer pie rings
        const totalsByType = typeIds.map((tId) => {
            const sum = storageIds.reduce((acc, sId) => acc + safeMetricValue(storageTypeAgg[sId]?.[tId] ?? {count:0, volume:0, treeMarkingVolume:0}), 0);
            return { id: tId, label: typeLabels[tId] || tId, value: Number.isFinite(sum) ? sum : 0 };
        }).filter(p => Number.isFinite(p.value) && p.value !== 0);

        const totalsByStorage = storageIds.map((sId) => {
            const sum = (Object.values(storageTypeAgg[sId] || {}) as TypeAgg[])
                .reduce((acc, agg) => acc + safeMetricValue(agg), 0);
            return { id: sId, label: storageLabels[sId] || sId, value: Number.isFinite(sum) ? sum : 0 };
        }).filter(p => Number.isFinite(p.value) && p.value !== 0);

        if (showStorage) {
            const xCategories = storageIds.map(id => storageLabels[id] || id);

            const series = typeIds
                .map((tId) => {
                    const data = storageIds.map((sId) => {
                        const agg = storageTypeAgg[sId]?.[tId] ?? {count: 0, volume: 0, treeMarkingVolume: 0};
                        return safeMetricValue(agg);
                    });
                    return {
                        label: typeLabels[tId] || tId,
                        data,
                        stack: 'total' as const,
                    };
                })
                // filter out entirely undefined/empty series just in case
                .filter(s => Array.isArray(s.data) && s.data.some(v => Number.isFinite(v)));

            // Inner ring: totals by type; Outer ring: totals by storage
            const pieInner = totalsByType;
            const pieOuter = totalsByStorage;

            return { xCategories, series, pie: pieInner, pieInner, pieOuter } as const;
        } else {
            const xCategories = typeIds.map(id => typeLabels[id] || id);
            const data = typeIds.map((tId) => {
                let sum = 0;
                for (const sId of storageIds) {
                    const agg = storageTypeAgg[sId]?.[tId] ?? {count: 0, volume: 0, treeMarkingVolume: 0};
                    sum += safeMetricValue(agg);
                }
                return Number.isFinite(sum) ? sum : 0;
            });

            const pie = typeIds.map((tId, i) => ({
                id: tId,
                label: typeLabels[tId] || tId,
                value: Number.isFinite(data[i]) ? data[i] : 0,
            })).filter(p => Number.isFinite(p.value));

            // Inner ring: totals by type; Outer ring: totals by storage
            const pieInner = totalsByType;
            const pieOuter = totalsByStorage;

            return { xCategories, series: [{ label: 'Total', data }], pie, pieInner, pieOuter } as const;
        }
    }, [storageTypeAgg, storageLabels, typeLabels, metric, showStorage]);

    if (groupedData.isLoading || logData.isLoading || cordwoodData.isLoading || treeMarkingsQuery.isLoading || !storageTypeAgg || !storageLabels) {
        return (
            <Paper
                sx={{padding: "20px", justifyContent: "center", alignItems: "center", display: "flex"}}>
                <GradientCircularProgress/>
            </Paper>
        )
    }

    return (
        <Paper sx={{p: 2}}>
            <Typography variant="h6">
                <Trans>Report TreeMarkings</Trans> {mainTitle ? `- ${mainTitle}` : ''}
            </Typography>

            {/* Per-storage charts */}
            <Stack spacing={3} sx={{ my: 2 }}>
                {Object.keys(storageTypeAgg).map((sId) => {
                    const typeMap = storageTypeAgg[sId] || {} as any;
                    const typeIds = Object.keys(typeMap);
                    const xLabels = typeIds.map(tId => typeLabels[tId] || tId);
                    const allocSeries = typeIds.map(tId => Number((typeMap as any)[tId]?.treeMarkingVolume || 0));
                    const logsSeries = typeIds.map(tId => Number((typeMap as any)[tId]?.logsVolume || 0));
                    const cordSeries = typeIds.map(tId => Number((typeMap as any)[tId]?.cordwoodVolume || 0));

                    return (
                        <Paper key={`storage-charts-${sId}`} sx={{ p: 2 }}>
                            <Typography variant="subtitle1">
                                <Trans>Storage</Trans>: {storageLabels[sId] || sId}
                            </Typography>
                            <Box sx={{ mt: 2 }}>
                                { allocSeries.length > 0 && logsSeries.length > 0 && cordSeries.length > 0 && xLabels.length > 0 &&
                                    <BarChart
                                        height={400}
                                        xAxis={[{ scaleType: 'band', data: xLabels }]}
                                        yAxis={[{ width: 50 }]}
                                        series={[
                                            { data: allocSeries, label: t`Tree Marking`, type: 'bar', color: '#1976d2' },
                                            { data: logsSeries, label: t`Logs`, type: 'bar', color: '#ed6c02', stack: 'stack1' },
                                            { data: cordSeries, label: t`Cordwood`, type: 'bar', color: '#2e7d32', stack: 'stack1' },
                                        ]}
                                    />
                                }
                            </Box>
                        </Paper>
                    );
                })}
            </Stack>

            <Divider sx={{ my: 2 }} />

            <TableContainer component={Paper}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            {showStorage && <StyledTableCell><Trans>Storage</Trans></StyledTableCell>}
                            <StyledTableCell><Trans>Type</Trans></StyledTableCell>
                            <StyledTableCell align="right"><Trans>Tree Marking volume</Trans></StyledTableCell>
                            <StyledTableCell align="right"><Trans>Logs Count</Trans></StyledTableCell>
                            <StyledTableCell align="right"><Trans>Volume</Trans></StyledTableCell>
                            <StyledTableCell align="right"><Trans>Total volume</Trans></StyledTableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {Object.entries(storageTypeAgg).map(([sId, typeMap]) => (
                            <React.Fragment key={sId}>
                                {Object.entries(typeMap).map(([tId, agg], idx) => (
                                    <TableRow key={sId + '_' + tId}>
                                        {showStorage &&
                                            <StyledTableStorage>
                                                {idx === 0 ?
                                                    <TimberStorageLink id={sId} icon={<StyledMdiIcon  d={mdiLinkVariant}/>}/>
                                                    :
                                                    undefined
                                                }
                                            </StyledTableStorage>
                                        }
                                        <TableCell><TimberTypeLink id={tId} icon={<StyledMdiIcon d={mdiLinkVariant}/>}/></TableCell>
                                        <TableCell align="right">{fixedFormat(agg.treeMarkingVolume)}</TableCell>
                                        <TableCell align="right">{agg.count}</TableCell>
                                        <TableCell align="right">{fixedFormat(agg.volume)}</TableCell>
                                        <TableCell
                                            align="right">{fixedFormat(agg.treeMarkingVolume - agg.volume)}</TableCell>
                                    </TableRow>
                                ))}
                            </React.Fragment>
                        ))}
                        <TableRow>
                            <StyledTableFooter colSpan={showStorage ? 2 : 1}><Trans>Total</Trans></StyledTableFooter>
                            <StyledTableFooter align="right">{fixedFormat(totals.treeMarkingVolume)}</StyledTableFooter>
                            <StyledTableFooter align="right">{totals.count}</StyledTableFooter>
                            <StyledTableFooter align="right">{fixedFormat(totals.volume)}</StyledTableFooter>
                            <StyledTableFooter
                                align="right">{fixedFormat(totals.treeMarkingVolume - totals.volume)}</StyledTableFooter>
                        </TableRow>
                    </TableBody>
                </Table>
            </TableContainer>

            <Box sx={{mt: 2}}>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ alignItems: { xs: 'stretch', md: 'center' }, justifyContent: "space-between" }}>
                    <ToggleButtonGroup
                        color="primary"
                        value={metric}
                        exclusive
                        onChange={(_, v) => v && setMetric(v)}
                        size="small"
                    >
                        <ToggleButton value="treeMarking"><Trans>Tree Marking</Trans></ToggleButton>
                        <ToggleButton value="volume"><Trans>Volume</Trans></ToggleButton>
                        <ToggleButton value="count"><Trans>Count</Trans></ToggleButton>
                    </ToggleButtonGroup>
                </Stack>
            </Box>
            <Divider sx={{ my: 2 }} />
            {chartData.pieInner && chartData.pieOuter && chartData.pie &&
                <Box sx={{ mt: 2 }}>
                    <PieChart
                        height={400}
                        series={
                            multiLevelPie && (chartData as any).pieInner && (chartData as any).pieOuter
                                ? [
                                    { innerRadius: 30, outerRadius: 147, paddingAngle: 0, cornerRadius: 6, data: (chartData as any).pieInner,
                                        arcLabel: (item) => metric == 'count' ?  item.value : fixedFormat(item.value), },
                                    { innerRadius: 150, outerRadius: 200, paddingAngle: 0, cornerRadius: 6, data: (chartData as any).pieOuter,
                                        arcLabel: (item) => metric == 'count' ?  item.value : fixedFormat(item.value),},
                                ]
                                : [
                                    { innerRadius: 30, outerRadius: 200, paddingAngle: 0, cornerRadius: 6, data: (chartData as any).pie,
                                        arcLabel: (item) => metric == 'count' ?  item.value : fixedFormat(item.value), }
                                ]
                        }
                        slotProps={{ legend: { hidden: false } }}
                    />
                </Box>
            }
        </Paper>
    );
};

export default ReportTreeMarkings;
